/**
 * Helpers for business event additional field definitions and payloads.
 * Matches crm_event_fields_definition / business_event_fields_definition param types.
 */

export function parseRequiredParams(definition) {
  if (!definition || !definition.required_params) {
    return [];
  }
  try {
    const parsed = JSON.parse(definition.required_params);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
}

export function isOptionalParam(paramSpec) {
  if (!paramSpec) {
    return false;
  }
  const description = String(paramSpec.description || '').toLowerCase();
  return paramSpec.param === 'placeholder' || description.includes('optional');
}

export function buildDefaultFieldAttributes(fieldDefinition) {
  const params = parseRequiredParams(fieldDefinition);
  const attributes = {};

  params.forEach((paramSpec) => {
    const key = paramSpec.param;
    if (paramSpec.param_type === 'array') {
      attributes[key] = [''];
      return;
    }
    if (key === 'rows') {
      attributes[key] = '4';
      return;
    }
    attributes[key] = '';
  });

  return attributes;
}

export function normalizeFieldAttributesForApi(attributes) {
  if (!attributes || typeof attributes !== 'object') {
    return {};
  }

  const normalized = {};

  Object.keys(attributes).forEach((key) => {
    const value = attributes[key];
    if (Array.isArray(value)) {
      normalized[key] = value.map((item) =>
        item === null || item === undefined ? '' : String(item)
      );
      return;
    }
    if (value === null || value === undefined) {
      normalized[key] = '';
      return;
    }
    normalized[key] = String(value);
  });

  return normalized;
}

export function normalizeAdditionalFieldsForApi(fields) {
  if (!Array.isArray(fields)) {
    return [];
  }

  return fields.map((section) => ({
    ...section,
    field_attributes: normalizeFieldAttributesForApi(section.field_attributes),
  }));
}

export function validateSectionFieldAttributes(section, fieldsDefinition, sectionIndex) {
  const errors = [];
  const indexLabel = sectionIndex + 1;
  const definition = fieldsDefinition[section.field_type];
  const params = parseRequiredParams(definition);
  const attributes = section.field_attributes || {};

  params.forEach((paramSpec) => {
    const key = paramSpec.param;
    const value = attributes[key];

    if (isOptionalParam(paramSpec)) {
      if (paramSpec.param_type === 'array') {
        return;
      }
      return;
    }

    if (paramSpec.param_type === 'array') {
      if (!Array.isArray(value) || !value.length) {
        errors.push(`Parameter "${key}" is required for section #${indexLabel}`);
        return;
      }
      const hasEmptyOption = value.some(
        (option) => option === null || option === undefined || String(option).trim() === ''
      );
      if (hasEmptyOption) {
        errors.push(`Parameter "${key}" is required for section #${indexLabel}`);
      }
      return;
    }

    if (value === null || value === undefined || String(value).trim() === '') {
      errors.push(`Parameter "${key}" is required for section #${indexLabel}`);
    }
  });

  return errors;
}

export function validateAdditionalSections(sections, fieldsDefinition) {
  const errors = [];

  (sections || []).forEach((section, index) => {
    if (!section.field_type) {
      errors.push(`Field Type is required for section #${index + 1}`);
    }
    if (!section.label || String(section.label).trim() === '') {
      errors.push(`Label is required for section #${index + 1}`);
    }
    errors.push(...validateSectionFieldAttributes(section, fieldsDefinition, index));
  });

  return errors;
}

export function detectParamInputType(fieldDefinition, paramKey) {
  const params = parseRequiredParams(fieldDefinition);
  const spec = params.find((param) => param.param === paramKey);
  if (!spec) {
    return 'text';
  }

  switch (String(spec.param_type || '').toLowerCase()) {
    case 'integer':
    case 'number':
      return 'number';
    case 'email':
      return 'email';
    case 'array':
      return 'array';
    default:
      return 'text';
  }
}
